import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, ChevronRight, Star, Truck, RotateCcw } from "lucide-react";
import { api, inr } from "../lib/api";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import { Reveal } from "../components/Reveal";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [openAcc, setOpenAcc] = useState("details");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    setQty(1);
    setImgIdx(0);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    api.get(`/related/${id}`).then((r) => setRelated(r.data.slice(0, 4))).catch(() => {});
  }, [id]);

  if (notFound) {
    return (
      <main className="pt-[68px] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="font-serif text-4xl text-zinc-200">Piece not found</p>
          <Link to="/shop" className="btn-gold mt-6">Back to Shop</Link>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="pt-[68px] min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-[#d4af37]/30 border-t-[#d4af37] animate-spin" />
      </main>
    );
  }

  const buyNow = () => {
    add(product, qty);
    navigate("/checkout");
  };

  const notifyMe = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(notifyEmail.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    setNotifyBusy(true);
    try {
      await api.post("/restock-alert", { product_id: product.id, email: notifyEmail.trim() });
      toast.success("You're on the list — we'll email you the moment it's back");
      setNotifyEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setNotifyBusy(false);
    }
  };

  const accordions = [
    { id: "details", title: "Details & Materials", body: (
      <dl className="space-y-2.5">
        {Object.entries(product.details || {}).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-6 text-sm border-b border-[#d4af37]/10 pb-2.5 last:border-0">
            <dt className="text-zinc-500">{k}</dt>
            <dd className="text-zinc-200 text-right">{v}</dd>
          </div>
        ))}
      </dl>
    )},
    { id: "care", title: "Care Guide", body: (
      <p className="text-sm text-zinc-400 leading-relaxed">
        Keep away from water, perfume and direct sunlight. Wipe gently with a dry cloth after
        wear and store in the pouch provided. Your plating stays luminous for years with a
        little care.
      </p>
    )},
    { id: "shipping", title: "Shipping & Returns", body: (
      <p className="text-sm text-zinc-400 leading-relaxed">
        Dispatched within 48 hours. Free shipping on orders above ₹999. 7-day easy returns on
        unworn pieces in original packaging.
      </p>
    )},
  ];

  return (
    <main className="pt-[68px] min-h-screen" data-testid="product-page">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <nav className="flex items-center gap-2 text-xs text-zinc-500 tracking-wide mb-8" data-testid="product-breadcrumb">
          <Link to="/" className="hover:text-[#f3e5ab]">Home</Link>
          <ChevronRight size={12} />
          <Link to={`/shop?category=${product.category}`} className="hover:text-[#f3e5ab]">{product.category}</Link>
          <ChevronRight size={12} />
          <span className="text-zinc-300">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-[#d4af37]/25 bg-[#121215] group">
              <img
                src={product.images?.[Math.min(imgIdx, (product.images?.length || 1) - 1)] || product.images?.[0]}
                alt={product.name}
                className="w-full h-[440px] md:h-[620px] object-cover"
                data-testid="product-gallery-main"
              />
              {(product.images?.length || 0) > 1 && (
                <>
                  <button
                    onClick={() => setImgIdx((imgIdx - 1 + product.images.length) % product.images.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass gold-hairline text-[#f3e5ab] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid="product-gallery-prev"
                    aria-label="Previous image"
                  >
                    <ChevronRight size={18} className="rotate-180" />
                  </button>
                  <button
                    onClick={() => setImgIdx((imgIdx + 1) % product.images.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass gold-hairline text-[#f3e5ab] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid="product-gallery-next"
                    aria-label="Next image"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <span className="absolute bottom-3 right-3 text-[11px] tracking-wide glass gold-hairline rounded-full px-3 py-1 text-zinc-300" data-testid="product-gallery-count">
                    {Math.min(imgIdx, product.images.length - 1) + 1} / {product.images.length}
                  </span>
                </>
              )}
            </div>
            {(product.images?.length || 0) > 1 && (
              <div className="mt-4 flex flex-wrap gap-3" data-testid="product-thumbs">
                {product.images.map((im, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    data-testid={`product-thumb-${i}`}
                    className={`w-[72px] rounded-lg overflow-hidden border transition-all duration-300 ${
                      i === Math.min(imgIdx, product.images.length - 1)
                        ? "border-[#d4af37] shadow-[0_0_20px_rgba(212,175,55,0.25)] opacity-100"
                        : "border-[#d4af37]/20 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={im} alt={`${product.name} view ${i + 1}`} className="w-full aspect-square object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="absolute -bottom-5 -right-3 w-32 h-32 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.3)_0%,transparent_70%)] blur-xl pointer-events-none" />
          </motion.div>

          <div>
            <p className="eyebrow mb-3">{product.category}</p>
            <h1 className="font-serif font-light text-4xl md:text-5xl text-zinc-100 leading-tight" data-testid="product-name">
              {product.name}
            </h1>
            <div className="flex items-center gap-2 mt-3">
              <div className="flex text-[#d4af37]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={13} fill={i < Math.round(product.rating) ? "currentColor" : "none"} strokeWidth={1.2} />
                ))}
              </div>
              <span className="text-xs text-zinc-500">{product.rating.toFixed(1)}</span>
            </div>

            <div className="flex items-baseline gap-3 mt-6" data-testid="product-price">
              <span className="font-serif text-3xl text-[#f3e5ab]">{inr(product.price)}</span>
              {product.mrp > product.price && (
                <span className="text-zinc-600 line-through text-lg">{inr(product.mrp)}</span>
              )}
              {product.mrp > product.price && (
                <span className="text-xs font-semibold text-emerald-400/90 bg-emerald-400/10 px-2.5 py-1 rounded-full">
                  Save {inr(product.mrp - product.price)}
                </span>
              )}
            </div>
            <p className="text-xs text-[#d4af37]/70 mt-2">Use code AURELIA10 for an extra 10% off at checkout</p>

            <p className="mt-6 text-zinc-400 leading-relaxed">{product.description}</p>

            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-[#d4af37]/30 rounded-full" data-testid="product-qty-stepper">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 text-zinc-400 hover:text-[#f3e5ab]" data-testid="product-qty-minus" aria-label="Decrease quantity"><Minus size={14} /></button>
                <span className="min-w-[28px] text-center text-sm text-zinc-100" data-testid="product-qty-value">{qty}</span>
                <button onClick={() => setQty(Math.min(10, qty + 1))} className="px-4 py-3 text-zinc-400 hover:text-[#f3e5ab]" data-testid="product-qty-plus" aria-label="Increase quantity"><Plus size={14} /></button>
              </div>
              {product.stock <= 5 && product.stock > 0 && (
                <p className="text-xs text-amber-300/90" data-testid="product-stock-warning">Only {product.stock} left in stock</p>
              )}
              {product.stock === 0 && <p className="text-xs text-red-400">Out of stock</p>}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              {product.stock === 0 ? (
                <div className="w-full rounded-xl border border-[#d4af37]/25 bg-[#101013] p-5" data-testid="notify-me-block">
                  <p className="font-serif text-xl text-zinc-100">Out of stock</p>
                  <p className="text-xs text-zinc-500 mt-1 mb-4">Leave your email — we&rsquo;ll tell you the moment it returns.</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="input-dark flex-1 !py-2.5"
                      data-testid="notify-email-input"
                      aria-label="Email for restock alert"
                    />
                    <button onClick={notifyMe} disabled={notifyBusy} className="btn-gold !px-5 !py-2.5" data-testid="notify-me-button">
                      {notifyBusy ? "..." : "Notify Me"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => add(product, qty)}
                    disabled={product.stock === 0}
                    className="btn-gold flex-1 disabled:opacity-40 disabled:pointer-events-none"
                    data-testid="product-add-to-cart-button"
                  >
                    <ShoppingBag size={16} /> Add to Bag
                  </button>
                  <button
                    onClick={buyNow}
                    disabled={product.stock === 0}
                    className="btn-ghost flex-1 disabled:opacity-40 disabled:pointer-events-none"
                    data-testid="product-buy-now-button"
                  >
                    Buy Now
                  </button>
                </>
              )}
            </div>

            <div className="mt-8 flex gap-6 text-xs text-zinc-500">
              <span className="flex items-center gap-2"><Truck size={14} className="text-[#d4af37]" /> Shipping ₹50 under ₹499 · ₹99 above</span>
              <span className="flex items-center gap-2"><RotateCcw size={14} className="text-[#d4af37]" /> 7-day returns</span>
            </div>

            <div className="mt-10 border-t border-[#d4af37]/15" data-testid="product-accordions">
              {accordions.map((a) => (
                <div key={a.id} className="border-b border-[#d4af37]/15">
                  <button
                    onClick={() => setOpenAcc(openAcc === a.id ? "" : a.id)}
                    className="w-full flex justify-between items-center py-5 text-left"
                    data-testid={`product-accordion-${a.id}`}
                  >
                    <span className="text-sm tracking-[0.14em] uppercase text-zinc-200">{a.title}</span>
                    <ChevronRight size={16} className={`text-[#d4af37] transition-transform duration-300 ${openAcc === a.id ? "rotate-90" : ""}`} />
                  </button>
                  {openAcc === a.id && <div className="pb-6">{a.body}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-24 pb-16" data-testid="product-related">
            <Reveal className="mb-10">
              <p className="eyebrow mb-3">Complete the Look</p>
              <h2 className="font-serif font-light text-3xl md:text-4xl text-zinc-100">You may also love</h2>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-7">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default ProductDetail;
